import { IconNames } from '@blueprintjs/icons';
import { Icon } from '@strata/react-status-icons';
import classNames from 'classnames';

import { type IntakeCase } from 'store/api/intakeCaseApi';
import { type FormSection, FormSections } from 'store/intakeForm/intakeFormSchema';

/** Intake Case fields required for save */
export type SaveRequiredFields = Pick<
    Partial<IntakeCase>,
    | 'intakeSource'
    | 'contactFirstName'
    | 'contactLastName'
    | 'contactPhoneNumber'
    | 'contactOfficeName'
    | 'segmentId'
    | 'memberFirstName'
    | 'memberLastName'
    | 'memberDateOfBirth'
    | 'memberId'
    | 'memberCity'
    | 'memberState'
    | 'memberZipcode'
    | 'productServiceId'
    | 'placeOfService'
    | 'benefit'
    | 'productServiceOid'
    | 'prescriberVerified'
    | 'prescriberFirstName'
    | 'prescriberLastName'
    | 'prescriberNpi'
    | 'prescriberPhoneNumber'
    | 'prescriberFaxNumber'
    | 'prescriberAddress1'
    | 'prescriberCity'
    | 'prescriberState'
    | 'prescriberZip'
    | 'memberEligible'
    | 'memberGender'
    | 'memberAddress1'
>;

interface SectionCompletionStatusProps {
    section: FormSection;
    values: SaveRequiredFields;
}

const getCompletionCounts = ({ section, values }: SectionCompletionStatusProps) => {
    let requiredFields: unknown[];
    switch (section) {
        case FormSections.POINT_OF_CONTACT:
            requiredFields = [
                values.intakeSource,
                values.contactFirstName,
                values.contactLastName,
                values.contactPhoneNumber,
                values.contactOfficeName,
            ];
            break;
        case FormSections.HEALTH_PLAN_AND_PATIENT:
            requiredFields = [
                values.segmentId,
                values.memberFirstName,
                values.memberLastName,
                values.memberDateOfBirth,
                values.memberId,
            ];
            break;
        case FormSections.PRODUCT:
            requiredFields = [values.benefit, values.productServiceOid, values.productServiceId];
            break;
        case FormSections.PRESCRIBER:
            requiredFields = [
                values.prescriberVerified,
                values.prescriberNpi,
                values.prescriberFirstName,
                values.prescriberLastName,
                values.prescriberPhoneNumber,
                values.prescriberAddress1,
                values.prescriberCity,
                values.prescriberState,
                values.prescriberZip,
            ];
            break;
        case FormSections.PATIENT:
            requiredFields = [
                values.memberId,
                values.memberCity,
                values.memberState,
                values.memberZipcode,
                values.memberGender,
                values.memberAddress1,
                values.memberCity,
                values.memberState,
                values.memberZipcode,
            ];
            break;
        default:
            requiredFields = [];
    }
    return {
        completed: requiredFields.filter((val) => !!val).length,
        required: requiredFields.length,
    };
};

export const SectionCompletionStatus = ({ section, values }: SectionCompletionStatusProps) => {
    const { completed, required } = getCompletionCounts({ section, values });

    const isSectionCompleted = completed === required;
    const icon = isSectionCompleted ? IconNames.TickCircle : IconNames.Error;

    const iconColor = isSectionCompleted
        ? 'sds_section-completion-status--completed'
        : 'sds_section-completion-status--save';

    return (
        <span className={classNames('sds_section-completion-status', iconColor)}>
            <Icon icon={icon} />
            <span>{`${completed}/${required} Completed to Save`}</span>
        </span>
    );
};
